import React, { useState, useCallback } from 'react';
import { Popover } from '@mantine/core';

import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages } from 'react-intl';
import { Link } from 'react-router-dom';

import { CommonPopover } from '@shipstation/client-common/components/common-popover';

import { Button } from 'ui-components/components/button';
import { useMaxZ } from 'ui-components/hooks/services/use-max-z';

import { getCarriersPath } from '../../routes/constants';
import style from './apply-button-popover.module.css';

const messages = defineMessages({
  enableService: {
    description: 'lets the user know why they cannot apply a service',
    defaultMessage: 'To take advantage of these savings, enable the {serviceName} service via <link>Settings > Carriers & Services</link>.',
    id: 'Yf3QrQ',
  },
});

const PopoverContent = ({ serviceName }) => {
  return (
    <div>
      <FormattedMessage
        {...messages.enableService}
        values={{
          serviceName,
          link: (chunks) => <Link to={getCarriersPath()}>{chunks}</Link>,
        }}
      />
    </div>
  );
};
PopoverContent.propTypes = {
  serviceName: PropTypes.string.isRequired,
};

export const ApplyButton = ({ service, onClick, amplitudeTrackingId = '', children, theme = 'link', buttonClassName }) => {
  const { isHidden } = service;
  const [isOpen, setIsOpen] = useState(false);
  const zIndex = useMaxZ();
  const closePopover = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const openPopover = useCallback(() => {
    if (isHidden) {
      setIsOpen(true);
    }
  }, [setIsOpen, isHidden]);

  const onOpenChange = useCallback((nextOpen) => (nextOpen ? undefined : closePopover()), [closePopover]);

  return (
    <CommonPopover
      classNames={{ dropdown: style['apply-button-dropdown'] }}
      onChange={onOpenChange}
      opened={isOpen}
      position='bottom'
      withArrow
      zIndex={zIndex}
    >
      <Popover.Target>
        <span onMouseEnter={openPopover}>
          <Button
            className={buttonClassName}
            disabled={service.isHidden}
            onClick={onClick}
            amplitudeTrackingId={amplitudeTrackingId}
            theme={theme}
          >
            {children}
          </Button>
        </span>
      </Popover.Target>
      <Popover.Dropdown>
        <PopoverContent serviceName={service.name} />
      </Popover.Dropdown>
    </CommonPopover>
  );
};

ApplyButton.propTypes = {
  service: PropTypes.shape({
    name: PropTypes.string.isRequired,
    serviceId: PropTypes.string.isRequired,
    isHidden: PropTypes.bool.isRequired,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  amplitudeTrackingId: PropTypes.string,
  children: PropTypes.node.isRequired,
  theme: PropTypes.string,
  buttonClassName: PropTypes.string,
};
